import { getRegionConfig } from 'config';
import { EntryWithHandle } from 'connectors/contentful';
import { Region } from 'constants/shop';
import { Entry } from 'contentful';
import { IBlogAuthorFields } from 'global';
import { ContentfulProvider } from 'modules/content-cms/components/context';
import AuthorPageComponent from 'modules/magazine/author-page';
import {
  ArticleCard,
  fetchMagazineIndex,
  getArticleCards,
  getMagazineData,
} from 'modules/magazine/contentful';
import { getMagazineCanonicalUrl } from 'modules/magazine/canonical-url';
import MagazineHead from 'modules/magazine/magazine-head';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { BathUrlQuery } from 'pages/[region]/[magazine]';
import { ParsedUrlQuery } from 'querystring';
import { getLocaleFromNextLocale, getRegionFromRouteParam } from 'utils/market';

const AUTHOR_ARTICLE_LIMIT = 6;

interface PropTypes {
  contentfulData: EntryWithHandle<IBlogAuthorFields>;
  canonicalUrl: string;
  articles: Entry<ArticleCard>[];
}
const AuthorPage: NextPage<PropTypes> = ({ contentfulData, articles, canonicalUrl }) => (
  <ContentfulProvider
    fallbackData={contentfulData}
    handle={contentfulData.handle}
    revalidate={false}
  >
    <MagazineHead
      canonicalUrl={canonicalUrl}
      pageTitle={contentfulData.fields.name}
      author={contentfulData.fields}
    />
    <AuthorPageComponent articles={articles} />
  </ContentfulProvider>
);
interface UrlQuery extends ParsedUrlQuery, BathUrlQuery {
  region: Region;
  authorHandle: string;
}

export const getStaticProps: GetStaticProps<PropTypes, UrlQuery> = async (context) => {
  const region = getRegionFromRouteParam(context.params?.region);
  const locale = getLocaleFromNextLocale(context.params?.region, context.locale);
  const { magazineBasePath } = getRegionConfig(region);
  if (!magazineBasePath || context.params?.magazine !== magazineBasePath)
    return { notFound: true, revalidate: true };
  const index = await fetchMagazineIndex();
  const { articles, authors } = await getMagazineData(index, locale, region);
  const author = authors.find(({ handle }) => handle === context.params?.authorHandle);
  if (!author) return { notFound: true, revalidate: true };
  return {
    props: {
      contentfulData: { ...author, handle: author.fields.handle },
      canonicalUrl: getMagazineCanonicalUrl({ index, region, locale, author }),
      articles: getArticleCards(
        articles
          .filter(({ fields }) => fields.author?.sys.id === author.sys.id)
          .slice(0, AUTHOR_ARTICLE_LIMIT)
      ),
    },
    revalidate: 3600,
  };
};
export const getStaticPaths: GetStaticPaths<UrlQuery> = async () => ({
  paths: [],
  fallback: 'blocking',
});
export default AuthorPage;
